import React from 'react';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Collapse, Paper, Stack, Typography } from '@mui/material';

import { CHECKLIST_STATUS_STYLES } from './constants';
import ApprovalActionButton from './reportCards/ApprovalActionButton';
import ApprovalStatusChip from './reportCards/ApprovalStatusChip';
import ToggleDetailsButton from './reportCards/ToggleDetailsButton';
import type { ApprovalQueueItem } from './types';

interface Props {
  expanded: boolean;
  queueItem: ApprovalQueueItem;
  onApprovalChange: (rowId: string) => void;
  onToggleDetails: (itemId: string) => void;
}

export default function ApprovalQueueCard({ expanded, queueItem, onApprovalChange, onToggleDetails }: Props) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 1.25,
        overflow: 'hidden',
        ...CHECKLIST_STATUS_STYLES.approvalQueueCard,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
        <WarningAmberIcon color="warning" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle2" fontWeight={700}>
              {queueItem.title}
            </Typography>
            <ApprovalStatusChip approved={false} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {queueItem.description}
          </Typography>
        </Box>
        <ToggleDetailsButton expanded={expanded} onClick={() => onToggleDetails(queueItem.id)} />
        <ApprovalActionButton approved={false} onApprove={() => onApprovalChange(queueItem.id)} />
      </Stack>
      <Collapse in={expanded}>
        <Box sx={{ bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Data points
          </Typography>
          <Stack spacing={0} sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            {queueItem.details.map((detail) => (
              <Stack
                key={detail.label}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ borderBottom: 1, borderColor: 'divider', p: 1.5, '&:last-child': { borderBottom: 0 } }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ flex: '0 0 220px' }}>
                  {detail.label}
                </Typography>
                <Typography variant="body2">{detail.value}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
}
